/**
 * PB-4923 — mint a stable id for a new widget collection item.
 *
 * Every bounded field/item operation addresses an item by its own id, never by
 * array position, so an editor that creates an item without one produces
 * content the merge executor cannot touch: the whole collection falls back to
 * whole-value replacement and two people editing different items lose each
 * other's work again.
 *
 * This is the SIXTH id generator in the package, and deliberately the last one
 * added — the five that already exist (`uid` in `expanded-editors.tsx`,
 * `nextEditorId`, `nextRiskId`, `nextMilestoneId`, `EditableDataGrid.nextId`)
 * are each private to one editor family. New call sites use this one so a
 * seventh does not appear; the existing five are left alone because renaming a
 * generator changes nothing about the ids already persisted.
 *
 * `crypto.randomUUID` needs a secure context, which jsdom and an http:// dev
 * host do not always provide. The fallback only has to be unique within one
 * session: the id is written into content immediately, and a collision with an
 * id already in the collection is refused by the executor rather than merged.
 */
export function newWidgetItemId(prefix: string): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return `${prefix}-${cryptoApi.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}
